/**
 * Formatos compactos (e tolerantes a lixo) dos parâmetros do link
 * compartilhável do app-core. Funções puras, testadas.
 *
 *   c=-60.123,-3.456          centro (lon,lat em graus)
 *   z=3.25                    zoom
 *   g=GEOCOLOR,13:60,AirMass:40:0   camadas (produto[:opacidade%[:0 = oculta]])
 *   t=24h,10[,202609142340]   período, passo (min) e, se parado, o quadro (UTC)
 *   t=202609140300-202609142100,30  período personalizado (UTC)
 *   q=1800                    qualidade (omitido = automática)
 *   v=8,b,2                   velocidade (q/s), modo (r = repetir, b = vai e volta), pausa no fim (s)
 */
import { getProduct, MAX_GOES_LAYERS } from '../config/products';
import { isQualityMode, type QualityMode } from '../goes/budget';
import { formatCompactUtc, parseCompactUtc } from '../lib/time';
import { isStep, PERIOD_PRESETS, type Period, type PeriodPreset, type StepMinutes } from '../loop/frames';
import type { LoopMode } from '../loop/store';
import type { TimeRange } from './time';

const round = (n: number, digits: number) => {
  const f = 10 ** digits;
  return Math.round(n * f) / f;
};

// --- c / z -----------------------------------------------------------------

export function encodeCenter(lonLat: [number, number]): string {
  return `${round(lonLat[0], 3)},${round(lonLat[1], 3)}`;
}

export function decodeCenter(value: string): [number, number] | null {
  const parts = value.split(',').map(Number);
  if (parts.length !== 2 || parts.some((n) => !Number.isFinite(n))) return null;
  const [lon, lat] = parts;
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null;
  return [lon, lat];
}

export function encodeZoom(zoom: number): string {
  return String(round(zoom, 2));
}

export function decodeZoom(value: string): number | null {
  if (value.trim() === '') return null;
  const z = Number(value);
  return Number.isFinite(z) && z >= 0 && z <= 30 ? z : null;
}

// --- g ---------------------------------------------------------------------

export interface LayerSpec {
  productId: string;
  opacity: number;
  visible: boolean;
}

export function encodeLayers(layers: readonly LayerSpec[]): string {
  return layers
    .map((l) => {
      const op = Math.round(l.opacity * 100);
      if (!l.visible) return `${l.productId}:${op}:0`;
      return op === 100 ? l.productId : `${l.productId}:${op}`;
    })
    .join(',');
}

export function decodeLayers(value: string): LayerSpec[] | null {
  const specs: LayerSpec[] = [];
  for (const token of value.split(',')) {
    const [id, op, vis] = token.split(':');
    if (!id || !getProduct(id)) continue;
    const opNum = op === undefined || op === '' ? 100 : Number(op);
    const opacity = Number.isFinite(opNum) ? Math.max(0, Math.min(100, opNum)) / 100 : 1;
    specs.push({ productId: id, opacity, visible: vis !== '0' });
    if (specs.length >= MAX_GOES_LAYERS) break;
  }
  return specs.length ? specs : null;
}

// --- t ---------------------------------------------------------------------

export interface TimeSpec {
  period: Period;
  custom: TimeRange | null;
  step: StepMinutes;
  /** Quadro exibido (se o loop estava parado fora do último quadro). */
  frameTime: number | null;
}

export function encodeTime(spec: TimeSpec): string {
  const period =
    spec.period === 'custom' && spec.custom
      ? `${formatCompactUtc(spec.custom.start)}-${formatCompactUtc(spec.custom.end)}`
      : spec.period === 'custom'
        ? '3h'
        : spec.period;
  const parts = [period, String(spec.step)];
  if (spec.frameTime !== null) parts.push(formatCompactUtc(spec.frameTime));
  return parts.join(',');
}

export function decodeTime(value: string): TimeSpec | null {
  const [periodToken, stepToken, frameToken] = value.split(',');
  if (!periodToken) return null;
  let period: Period;
  let custom: TimeRange | null = null;
  if ((PERIOD_PRESETS as string[]).includes(periodToken)) {
    period = periodToken as PeriodPreset;
  } else {
    const m = /^(\d{12})-(\d{12})$/.exec(periodToken);
    const start = m ? parseCompactUtc(m[1]) : null;
    const end = m ? parseCompactUtc(m[2]) : null;
    if (start === null || end === null) return null;
    period = 'custom';
    custom = { start: Math.min(start, end), end: Math.max(start, end) };
  }
  const stepNum = Number(stepToken);
  const step: StepMinutes = isStep(stepNum) ? stepNum : 10;
  const frameTime = frameToken ? parseCompactUtc(frameToken) : null;
  return { period, custom, step, frameTime };
}

// --- q ---------------------------------------------------------------------

export function encodeQuality(q: QualityMode): string | null {
  return q === 'auto' ? null : String(q);
}

export function decodeQuality(value: string): QualityMode | null {
  if (value === 'auto' || value === 'a') return 'auto';
  const n = Number(value);
  return isQualityMode(n) ? n : null;
}

// --- v ---------------------------------------------------------------------

export interface SpeedSpec {
  fps: number;
  mode: LoopMode;
  dwellMs: number;
}

export function encodeSpeed(s: SpeedSpec, defaults: SpeedSpec): string | null {
  if (s.fps === defaults.fps && s.mode === defaults.mode && s.dwellMs === defaults.dwellMs) return null;
  return `${round(s.fps, 1)},${s.mode === 'bounce' ? 'b' : 'r'},${round(s.dwellMs / 1000, 1)}`;
}

/**
 * Lê `v=`. Os valores são normalizados para o que a interface oferece: q/s
 * inteiro dentro dos limites e pausa no fim na opção existente mais próxima.
 */
export function decodeSpeed(
  value: string,
  limits: { minFps: number; maxFps: number; dwellOptionsMs: readonly number[] },
): Partial<SpeedSpec> | null {
  const [fpsToken, modeToken, dwellToken] = value.split(',');
  const out: Partial<SpeedSpec> = {};
  const fps = Number(fpsToken);
  if (fpsToken && Number.isFinite(fps)) out.fps = Math.round(Math.max(limits.minFps, Math.min(limits.maxFps, fps)));
  if (modeToken === 'b') out.mode = 'bounce';
  else if (modeToken === 'r') out.mode = 'loop';
  const dwell = Number(dwellToken);
  if (dwellToken && Number.isFinite(dwell) && limits.dwellOptionsMs.length) {
    const ms = dwell * 1000;
    out.dwellMs = limits.dwellOptionsMs.reduce((best, o) => (Math.abs(o - ms) < Math.abs(best - ms) ? o : best));
  }
  return Object.keys(out).length ? out : null;
}
